import { Injectable } from '@angular/core';
import {
  AdjacencyTypes,
  BetweennessCentrality,
  IGraph,
  INode,
} from '@yfiles/yfiles';
import { ACTION_FREQUENCY_TYPE } from '../common/linqset-model';
import { AnalysisNode } from '../common/analysis-model';
import { calculateFriction, findRoutesBack } from './analysis-friction';
import { isOtherModel } from '../analysis/graph-levels';

const analysisNode = (node: INode): AnalysisNode | undefined => node.tag?.node;

@Injectable({
  providedIn: 'root',
})
export class AnalysisService {
  // how much of the process flows through each node, from 0 to 1, kept on the node as its centrality
  centrality(graph: IGraph) {
    const result = new BetweennessCentrality({ directed: true }).run(graph);

    // each entry pairs a node with its value; the second argument of forEach is only its position in the list
    result.normalizedNodeCentrality.forEach((entry) => {
      const node = analysisNode(entry.key);
      if (node) node.centrality = entry.value;
    });
  }

  // where routes that split join again at a node summing its inputs, each node on a slower route is given its route's
  // time as a multiple of the quickest; nodes on the quickest route, or no alternative, have none
  friction(graph: IGraph): { node: INode; paths: INode[][] }[] {
    const result: { node: INode; paths: INode[][] }[] = [];

    // worked out afresh each time, so values from an earlier set of models don't linger
    graph.nodes.forEach((n) => {
      const node = analysisNode(n);
      if (node) node.friction = 0;
    });

    // a model not shown, drawn where it joins the graph, is no route of the graph's own
    const previous = (n: INode) =>
      graph.edgesAt(n, AdjacencyTypes.INCOMING).map((e) => e.sourceNode).filter((s) => !isOtherModel(analysisNode(s))).toArray();

    graph.nodes
      .filter(
        (n) =>
          analysisNode(n)?.processType === ACTION_FREQUENCY_TYPE.SUM &&
          graph.inDegree(n) > 1,
      )
      .forEach((n) => {
        const friction = calculateFriction(
          findRoutesBack(n, previous),
          (node) => analysisNode(node)?.hours ?? 0,
        );
        if (!friction) return;

        friction.segments.forEach((segment, i) => {
          segment.forEach((s) => {
            const node = analysisNode(s);
            // a node on more than one route keeps the slowest
            if (node) node.friction = Math.max(node.friction || 0, friction.friction[i]);
          });
        });
        result.push({ node: n, paths: friction.segments });
      });
    return result;
  }
}
